import { Timeline, TimelineOptions, TimelineGroup, IdType } from "vis-timeline";
import { DataSet } from "vis-data";
import { IScope } from "angular";

import "vis-timeline/styles/vis-timeline-graph2d.css";
import { debounce } from "lodash";
import {
  TimelineApi,
  TimelineDataItem as ApiTimelineDataItem,
  TimelineIntervalSelectedArgs,
} from "./api";

export const vsDirectiveName = "visTimeline";

interface TimelineSelectProperties {
  items: IdType[]; // selected item ids (empty array on deselect)
  event: HammerEvent; // pointer event (Hammer wrapper)
}

type TimelineDataItem = ApiTimelineDataItem<unknown>;

// minimal Hammer surface, enough for what you're forwarding:
interface HammerEvent {
  srcEvent: MouseEvent | PointerEvent | TouchEvent;
  target: HTMLElement;
  center: { x: number; y: number };
  // deltaX/Y, type, etc. if you need them
}

timelineDirectiveDefinition.$inject = <string[]>[];
export function timelineDirectiveDefinition() {
  return {
    restrict: "E",
    scope: {
      items: "=",
      groups: "=",
      options: "=",
      onSelect: "&",
      onReady: "&?",
    },
    link: function (scope: DirectiveScope, element: JQLite) {
      const itemsDataSet = new DataSet<TimelineDataItem>(scope.items ?? []);
      const groupsDataSet = new DataSet<TimelineGroup>(scope.groups ?? []);

      const timeline = new Timeline(
        element[0],
        itemsDataSet,
        groupsDataSet,
        scope.options,
      );

      const api = new TimelineApiImpl(timeline, groupsDataSet, itemsDataSet);
      groupsDataSet.on(
        "update",
        debounce(() => api.bubbleUpHiddenEvents(), 250),
      );

      timeline.on("select", function (props: TimelineSelectProperties) {
        scope.$apply(function () {
          const itemId = props.items.at(0);
          const item = itemId === undefined ? undefined : api.getItem(itemId);

          const args: TimelineIntervalSelectedArgs = {
            selectedItem: item ?? undefined,
          };

          scope.onSelect({ args });
        });
      });

      scope.$watch("items", (items: TimelineDataItem[]) => {
        if (items) {
          api.clearItems();
          api.addItems(items);
        }
      });
      scope.$watch("groups", (groups: TimelineGroup[]) => {
        if (groups) {
          api.clearGroups();
          api.addGroups(groups);
        }
      });
      scope.$watch("options", (options: TimelineOptions) => {
        if (options) {
          api.setOptions(options);
        }
      });
      scope.$on("$destroy", function () {
        timeline.destroy();
      });

      if (scope.onReady) {
        scope.onReady({ api });
      }
    },
  };
}

class TimelineApiImpl implements TimelineApi {
  readonly originalItemById = new Map<IdType, TimelineDataItem>();

  private parentGroupMap: Record<IdType, IdType> = {};

  constructor(
    private readonly timeline: Timeline,
    private readonly groupDataSet: DataSet<TimelineGroup>,
    private readonly itemDataSet: DataSet<TimelineDataItem>,
  ) {}

  get groups(): TimelineGroup[] {
    return this.groupDataSet.get();
  }

  get items(): TimelineDataItem[] {
    return this.itemDataSet.get();
  }

  addItems(items: TimelineDataItem[]): void {
    const ids = this.itemDataSet.add(items);
    ids.forEach((id, i) => {
      this.originalItemById.set(id, items[i]);
    });
  }

  clearItems(): void {
    this.itemDataSet.clear();
  }

  addGroups(groups: TimelineGroup[]): void {
    this.groupDataSet.add(groups);
    this.parentGroupMap = this.buildParentMap();
  }

  clearGroups(): void {
    this.groupDataSet.clear();
    this.parentGroupMap = this.buildParentMap();
  }

  setOptions(options: TimelineOptions): void {
    this.timeline.setOptions(options);
  }

  getItem(id: IdType): TimelineDataItem | undefined {
    return this.itemDataSet.get(id) ?? undefined;
  }

  bubbleUpHiddenEvents() {
    const toRemove: IdType[] = [];
    const toAdd: TimelineDataItem[] = [];

    this.itemDataSet
      .get({
        filter: (it) => (it as TimelineDataItem)?.bubbleNestedEventUp ?? false,
      })
      .forEach((it) => {
        const home = this.originalItemById.get(it.id)?.group;
        const target = this.deepestVisibleAncestor(home);
        if (it.group !== target) {
          toRemove.push(it.id);
          toAdd.push({ ...it, group: target }); // spread keeps id + all custom fields
        }
      });

    //need to remove and add because setting an item's group programmatically doesn't
    //have the effect of changing the group in the UI
    if (toRemove.length) {
      this.itemDataSet.remove(toRemove);
      this.itemDataSet.add(toAdd); // <-- NOT this.addItems(...)
    }
  }

  // bubbleUpHiddenEvents() {
  //   const updates: Partial<TimelineDataItem>[] = [];

  //   this.itemDataSet
  //     .get({
  //       filter: (it) => (it as TimelineDataItem)?.bubbleNestedEventUp ?? false,
  //     })
  //     .forEach((it) => {
  //       const target = this.deepestVisibleAncestor(
  //         this.originalItemById.get(it.id)?.group,
  //       );
  //       if (it.group !== target) updates.push({ id: it.id, group: target });
  //     });
  //   if (updates.length) {
  //     this.itemDataSet.update(updates);
  //   }
  // }

  private buildParentMap(): Record<IdType, IdType> {
    const parentOf: Record<IdType, IdType> = {};
    this.groupDataSet.forEach((g) =>
      (g.nestedGroups || []).forEach((childId) => {
        parentOf[childId] = g.id;
      }),
    );
    return parentOf;
  }

  private deepestVisibleAncestor(homeId: IdType) {
    let g = homeId;
    while (true) {
      if (this.isRowVisible(g)) return g;
      const p = this.parentGroupMap[g];
      if (p === undefined) return g; // safety net
      g = p;
    }
  }

  private isRowVisible(groupId: IdType): boolean {
    const parentOf = this.parentGroupMap;
    let p = parentOf[groupId];
    while (p !== undefined) {
      const g = this.groupDataSet.get(p);
      if (g && g.showNested === false) return false;
      p = parentOf[p];
    }
    return true;
  }
}

type DirectiveScope = IScope & {
  items: TimelineDataItem[] | undefined;
  groups: TimelineGroup[] | undefined;
  options: TimelineOptions;
  onSelect(deps: { args: TimelineIntervalSelectedArgs }): void;
  onReady?(deps: { api: TimelineApi }): void;
};
